import type { AuditEntry } from '../audit';
import { hash32, rng, uuid } from './prng';

/**
 * About thirty access-trail events over the last week for the three console users, on a
 * Bangkok working day: sign-ins in the morning, a rule-set release published every other
 * day (drafts go live directly — the maker-checker step is dropped for now), exports, one
 * mistyped password and one refused call. Actors are the admin service's own `upn (id)` strings, so real sign-ins made
 * during the session sit among them naturally.
 */

export const DEMO_USERS = {
  admin: 'admin@kkp-poc.local (u-admin)',
  checker: 'checker@kkp-poc.local (u-checker)',
  viewer: 'viewer@kkp-poc.local (u-viewer)',
} as const;

const DAY = 86_400_000;
const BKK_OFFSET_MS = 7 * 3600_000;

export function demoAudit(now = Date.now(), seed = 'kkp-dime-demo'): AuditEntry[] {
  const out: AuditEntry[] = [];
  const todayBkk = Math.floor((now + BKK_OFFSET_MS) / DAY) * DAY - BKK_OFFSET_MS;
  for (let d = 6; d >= 0; d--) {
    const day = todayBkk - d * DAY;
    const r = rng(hash32(seed, 'audit', Math.floor(day / DAY)));
    const at = (h: number, m: number) => day + (h * 60 + m) * 60_000 + r.int(0, 59) * 1000;
    const push = (when: number, who: string, what: string, subject?: string, approvedBy?: string) =>
      out.push({
        id: uuid(r),
        whenUtc: new Date(when).toISOString(),
        who,
        what,
        ...(subject ? { subject } : {}),
        ...(approvedBy ? { approvedBy } : {}),
      });

    if (d === 4) push(at(8, 47), 'checker', 'login.failure');
    push(at(8, 50 + r.int(0, 8)), DEMO_USERS.admin, 'login.success');
    push(at(9, r.int(0, 20)), DEMO_USERS.checker, 'login.success');
    if (d % 2 === 1 || d === 0) push(at(9, 25 + r.int(0, 20)), DEMO_USERS.viewer, 'login.success');
    if (d % 2 === 0) {
      push(at(10, r.int(0, 50)), d % 4 === 0 ? DEMO_USERS.admin : DEMO_USERS.checker, 'rules.published');
    }
    if (d % 3 === 0) push(at(14, r.int(0, 50)), DEMO_USERS.viewer, 'report.exported', 'decisions.csv');
    if (d === 3) push(at(15, r.int(0, 50)), DEMO_USERS.viewer, 'access.denied', 'PUT /api/rules');
    if (d === 1) push(at(16, r.int(0, 30)), DEMO_USERS.admin, 'report.exported', 'rule-hits.csv');
    push(at(17, 30 + r.int(0, 25)), DEMO_USERS.admin, 'logout');
  }
  // Nothing from the last half hour: real events own the present.
  return out.filter((e) => Date.parse(e.whenUtc) < now - 30 * 60_000).sort((a, b) => (a.whenUtc < b.whenUtc ? 1 : -1));
}

/** Real entries and demo entries in one newest-first trail. */
export function mergeAudit(real: readonly AuditEntry[], demo: readonly AuditEntry[], limit: number): AuditEntry[] {
  return [...real, ...demo].sort((a, b) => (a.whenUtc < b.whenUtc ? 1 : a.whenUtc > b.whenUtc ? -1 : 0)).slice(0, limit);
}
